import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Gauge } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { scrollToId } from "../lib/scroll";

const FIRM_MOD = {
  "Fintech / Scale-up": -6,
  "Investment Bank": -10,
  "Asset Manager": -4,
  "Payment Institution": -8,
};

const FOCUS_BASE = {
  "FCA Conduct & Consumer Duty": 74,
  "AML / KYC & Financial Crime": 64,
  "SMCR & Governance": 68,
  "Cross-Border Operations": 57,
};

const RECO = {
  "FCA Conduct & Consumer Duty": "Review conduct risk frameworks, customer outcome testing and board-level MI against Consumer Duty expectations.",
  "AML / KYC & Financial Crime": "Prioritise a financial crime gap analysis — CDD refresh cycles, transaction monitoring tuning and SAR governance.",
  "SMCR & Governance": "Re-validate senior manager statements of responsibility, certification workflows and conduct rules training coverage.",
  "Cross-Border Operations": "Map jurisdictional licensing, delegation oversight and data flows across every market you operate in.",
};

const verdict = (score) =>
  score >= 70
    ? { label: "Strong foundation", tone: "text-[#3d7a52]" }
    : score >= 50
      ? { label: "Moderate exposure", tone: "text-[#a8824f]" }
      : { label: "Elevated risk", tone: "text-[#b0483c]" };

export default function RiskEstimator() {
  const [firm, setFirm] = useState("");
  const [focus, setFocus] = useState("");
  const [score, setScore] = useState(null);

  const calculate = () => {
    if (!firm || !focus) return;
    const raw = FOCUS_BASE[focus] + FIRM_MOD[firm];
    setScore(Math.max(18, Math.min(95, raw)));
  };

  const v = score !== null ? verdict(score) : null;

  return (
    <section id="readiness" data-testid="readiness-section" className="py-28 px-6 bg-[#faf9f6]">
      <div className="max-w-5xl mx-auto">
        <Reveal className="text-center mb-12">
          <Eyebrow>Interactive Assessment</Eyebrow>
          <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl text-[#1b2a4e] tracking-tight">
            How ready is your <span className="italic text-[#a8824f]">compliance posture?</span>
          </h2>
          <p className="text-[#45506b] mt-4 max-w-xl mx-auto text-sm sm:text-base">
            A two-minute indicative readiness estimate, shaped by your firm type and regulatory focus.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="rounded-3xl bg-white border border-[#e6e1d5] p-8 sm:p-12 shadow-[0_24px_70px_rgba(27,42,78,0.1)]">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="risk-firm-select" className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#a8824f] block mb-2.5">
                  Firm Type
                </label>
                <select
                  id="risk-firm-select"
                  data-testid="risk-firm-select"
                  value={firm}
                  onChange={(e) => { setFirm(e.target.value); setScore(null); }}
                  className="w-full bg-white border border-[#e6e1d5] rounded-xl px-4 py-3.5 text-sm text-[#1b2a4e] focus:outline-none focus:ring-2 focus:ring-[#a8824f] appearance-none"
                >
                  <option value="" disabled>Select your firm type</option>
                  {Object.keys(FIRM_MOD).map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="risk-focus-select" className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#a8824f] block mb-2.5">
                  Regulatory Focus
                </label>
                <select
                  id="risk-focus-select"
                  data-testid="risk-focus-select"
                  value={focus}
                  onChange={(e) => { setFocus(e.target.value); setScore(null); }}
                  className="w-full bg-white border border-[#e6e1d5] rounded-xl px-4 py-3.5 text-sm text-[#1b2a4e] focus:outline-none focus:ring-2 focus:ring-[#a8824f] appearance-none"
                >
                  <option value="" disabled>Select your priority area</option>
                  {Object.keys(FOCUS_BASE).map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            </div>

            <button
              data-testid="risk-calculate-button"
              onClick={calculate}
              disabled={!firm || !focus}
              className="mt-8 w-full sm:w-auto flex items-center justify-center gap-2.5 bg-[#1b2a4e] hover:bg-[#a8824f] disabled:opacity-30 disabled:cursor-not-allowed text-white text-sm font-semibold px-8 py-4 rounded-full hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#a8824f]"
            >
              <Gauge size={17} /> Estimate My Readiness
            </button>

            <AnimatePresence>
              {score !== null && (
                <motion.div
                  data-testid="risk-result"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                  className="mt-10 pt-10 border-t border-[#e6e1d5]"
                >
                  <div className="flex flex-wrap items-baseline gap-4 mb-5">
                    <span data-testid="risk-score-value" className="font-display text-6xl text-[#1b2a4e] font-semibold">{score}</span>
                    <span className="font-mono-alt text-xs uppercase tracking-[0.25em] text-[#45506b]">/ 100 readiness</span>
                    <span data-testid="risk-verdict" className={`font-mono-alt text-xs uppercase tracking-[0.25em] ${v.tone}`}>
                      {v.label}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-[#f0ede4] overflow-hidden mb-6">
                    <motion.div
                      className="h-full rounded-full bg-[#a8824f]"
                      initial={{ width: 0 }}
                      animate={{ width: `${score}%` }}
                      transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                    />
                  </div>
                  <p data-testid="risk-recommendation" className="text-[#45506b] text-sm sm:text-base leading-relaxed max-w-2xl">
                    {RECO[focus]}
                  </p>
                  <button
                    data-testid="risk-book-analysis-cta"
                    onClick={() => scrollToId("#contact")}
                    className="group mt-7 inline-flex items-center gap-2 text-sm font-semibold text-[#1b2a4e] hover:text-[#a8824f] transition-colors"
                  >
                    Book a detailed gap analysis for this profile
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
